/* eslint-disable complexity */
import { numberToCurrency } from '@hc-frontend/utils'
import getBenefitsTooltips from 'utils/pivot/plan/getBenefitsTooltips'
import tooltipMessage from 'core/controllerPlans/tooltipMessage'
import getCorrespondingDuration from 'core/services/getPlansByProductId/getCorrespondingDuration'
import { toString as toProductName } from 'utils/pivot/product'
import isEpicPlan from 'utils/pivot/plan/isEpicPlan'
import isRXPlan from 'utils/pivot/plan/isRXPlan'
import moneyFormat from 'utils/format/money'
import getPlanDetailTooltip from 'utils/pivot/plan/getPlanDetailTooltip'
import { getBenefitLabel } from 'core/controllerPlans/getBenefits'
import getPlanTypeKey from 'core/controllerPlans/getPlanTypeKey'
import getValidPeriodKey from 'utils/pivot/plan/period/getValidPeriodKey'
import { propAccessor } from '@hc-frontend/utils'
import {
  AMERITAS_DENTAL,
  AMERITAS_VISION,
  DENTAL,
  EXTERNAL_BROCHURE_FLEX_BENEFITS,
  LATITUDE,
  PRIMARY_DNV_ADD_ONS,
  VISION_ID
} from 'core/constants/products'
import getBadgeDental from 'core/controllerPlans/getBadgeDental'
import detailsDentalFees from 'core/controllerPlans/detailsDentalFees'
import getById from 'utils/DOM/getById'
import isSalesCenterSubDomain from 'utils/pivot/isSalesCenterSubDomain'
import isAgentLogged from 'utils/pivot/agent/isAgentLogged'
import getExtendedTagMessage from 'utils/pivot/plan/getExtendedTagMessage'
import { COMPARE_PLANS } from 'sites/routes'
import { enableFlexBenefits } from 'utils/experiments/enableFlexBenefits'
import { parseBenefits } from 'utils/plans/parseExternalBenefits'

function isExpanded(planId) {
  const addonsPanel = getById(`plan-addons-options--${planId}`)
  if (addonsPanel) {
    return addonsPanel.classList.contains('show')
  }
  return true
}

function hasSelectedVision({
  dentalId,
  planId,
  selectedAddons,
  hasRenaissanceVision,
  renaissanceDentalPlanIds
}) {
  if (!renaissanceDentalPlanIds.includes(dentalId)) {
    return false
  }

  const visionInput = getById(`vision-${planId}-addon-${dentalId}`)

  if (visionInput) {
    return visionInput.checked
  }

  return (
    hasRenaissanceVision &&
    (!selectedAddons.includes(dentalId) ||
      (selectedAddons.includes(dentalId) && selectedAddons.includes(VISION_ID)))
  )
}

function getPlanBenefits(plan): {
  primaryBenefits: PlanBenefit[]
  secondaryBenefits: PlanBenefit[]
} {
  const primaryBenefits: PlanBenefit[] = isRXPlan(plan) ? ['RX Included'] : []
  const secondaryBenefits: PlanBenefit[] = []

  plan.benefits.forEach(benefit => {
    if (benefit === 'telemedicine' || benefit === 'supplemental') {
      primaryBenefits.push(getBenefitLabel(benefit).replace('included', ''))
    } else if (benefit === 'patientAdvocacy') {
      secondaryBenefits.push({
        key: benefit,
        title: getBenefitLabel(benefit),
        value:
          'Use our patient advocates to help find a local doctor and schedule appointments. Once the claims process is complete and you have the final bill that is your responsibility, Point Health can help negotiate down your final total or establish a payment plan that works for you.'
      })
    } else {
      secondaryBenefits.push({
        key: benefit,
        title: getBenefitLabel(benefit)
      })
    }
  })
  return { primaryBenefits, secondaryBenefits }
}

function renderPlan(template, planId, state, index, useMaxPolicy = false) {
  const {
    plans,
    plansByType,
    hasVisionActive,
    hasDentalActive,
    brochures,
    addonsByPlan,
    filters
  } = state

  const plan = plans[planId]
  const { primaryBenefits, secondaryBenefits } = getPlanBenefits(plan)
  // A primary dental or vision plan can be added directly to a plan,
  // as opposed to a Renaissance vision plan, which can only be added
  // if there's also a Renaissance dental plan selected.
  const primaryDnvIds = PRIMARY_DNV_ADD_ONS.flatMap(
    productType => plansByType[productType] || []
  )
  const renaissanceDentalPlanIds = plansByType[DENTAL] || []
  const ameritasDentalPlanIds = plansByType[AMERITAS_DENTAL] || []
  const ameritasVisionPlanIds = plansByType[AMERITAS_VISION] || []
  const latitudePlanIds = propAccessor(plansByType, String(LATITUDE), [])
  const hasPrimaryDnv = primaryDnvIds.length > 0
  const selectedAddons = propAccessor(addonsByPlan, String(planId), [])

  const planFilterKey = getPlanTypeKey(plan)

  const period = propAccessor(
    state.filtersByProductType[planFilterKey],
    'period',
    state.period
  )

  const validatedPeriodKey = +getValidPeriodKey(plan, period)

  let planDuration = getCorrespondingDuration({
    plan,
    mainPeriod: period === validatedPeriodKey ? period : validatedPeriodKey,
    additionalPeriods: state.additionalPeriods
  })

  if (!planDuration) {
    const durationKey = Object.keys(plan.durations)[0]
    planDuration = plan.durations[durationKey]
  }
  const productName = toProductName(plan.productId)
  const carrier = state.carriers[plan.carrierId]
  const policies = Object.keys(planDuration)
  const maxPolicy = policies[policies.length - 1]
  const maxPolicyDuration = planDuration[maxPolicy]

  const filterPolicies = propAccessor(
    state.filtersByProductType[planFilterKey],
    'policies',
    filters.policies
  )

  let recurrence = undefined

  if (planDuration[filterPolicies]) {
    recurrence = useMaxPolicy ? maxPolicy : filterPolicies
  } else {
    recurrence = maxPolicy
  }

  const policy = useMaxPolicy ? planDuration[maxPolicy] : planDuration[recurrence]

  const hasExtended = maxPolicyDuration.extendedDurationsAvailable.length > 0

  const compare = state.plansToCompare.includes(`${planId}-${period}`)

  const visited = state.plansVisited.includes(`${planId}-${period}`)

  const enrollment = policy.monthly.fees
    .filter(({ name }) => name === 'Application Fee')
    .reduce((acc, fee) => acc + fee.value, 0)
  const isEpic = isEpicPlan(plan)

  const extendedDuration = maxPolicyDuration.extendedDurationsAvailable[0]

  const extendPeriodTile = `${getExtendedTagMessage({
    period: policy.policyDuration,
    extendedDuration,
    currentPolicy: policy.recurrence,
    maxPolicies: maxPolicy
  })} Days`

  // Sort Dental Value Plan
  const dentalValuePlan = primaryDnvIds.find(planId =>
    /value/i.test(plans[planId].planName)
  )
  const primaryDnvPlans = dentalValuePlan
    ? [dentalValuePlan, ...primaryDnvIds.filter(id => id !== dentalValuePlan)]
    : primaryDnvIds
  const defaultRenaissanceDentalId = dentalValuePlan || renaissanceDentalPlanIds[0]
  const latitudePlanId = latitudePlanIds[0]
  const renaissanceVisionPlan = state.plans[VISION_ID]
  const hasRenaissanceVision = renaissanceVisionPlan != null
  const hasLatitude = latitudePlanIds.length > 0
  const hasFlexFenefits = enableFlexBenefits()
  const renderAddons = hasPrimaryDnv || hasLatitude || hasFlexFenefits

  const renderDental = hasPrimaryDnv && !(hasDentalActive || hasVisionActive)
  const canSaveQuotes = isAgentLogged()

  return template(
    {
      isSalesCenterSubDomain: isSalesCenterSubDomain(),
      index,
      plan,
      plans,
      productName,
      recurrence: policy.recurrence,
      carrier,
      period: policy.policyDuration,
      premium: +policy.monthly.premium,
      hasExtended,
      extendPeriodTile,
      maxPolicy,
      enrollment,
      expanded: renderDental || (hasLatitude || hasFlexFenefits) ? isExpanded(planId) : false,
      tooltip: {
        planDescription: getPlanDetailTooltip(plan, isEpic),
        cost: tooltipMessage(state, plan, policy['monthly']),
        ...getBenefitsTooltips()
      },
      planFilterKey,
      compare,
      visited,
      primaryBenefits,
      secondaryBenefits,
      renderAddons,
      renderLatitude: !hasFlexFenefits && hasLatitude,
      renderFlexBenefits: hasFlexFenefits,
      externalPlansSelected: state.externalPlansSelected,
      externalPlans: state.externalPlans.map(externalPlan => ({
        planId: externalPlan.id,
        isRecommended: externalPlan.altName === 'FlexProtectPro',
        planName: externalPlan.name,
        totalAmount: externalPlan.products.reduce((sum: number, product: any) => {
          return sum + (product?.price || 0)
        }, 0),
        benefits: parseBenefits(externalPlan.products)
      })),
      primaryDnvPlans,
      renderDental,
      hasDentalActive: hasDentalActive || hasVisionActive,
      hasRenaissanceVision,
      renaissanceVisionPlan,
      latitudePlans: plansByType[LATITUDE] || [],
      selectedAddons: selectedAddons || [],
      brochureLink: brochures[plan.brochureId],
      canSaveQuotes,
      brochures: {
        renaissanceDental: defaultRenaissanceDentalId
          ? brochures[plans[defaultRenaissanceDentalId].brochureId]
          : undefined,
        latitude: latitudePlanId
          ? brochures[plans[latitudePlanId].brochureId]
          : undefined,
        ameritasDental:
          ameritasDentalPlanIds.length > 0
            ? brochures[plans[ameritasDentalPlanIds[0]].brochureId]
            : undefined,
        ameritasVision:
          ameritasVisionPlanIds.length > 0
            ? brochures[plans[ameritasVisionPlanIds[0]].brochureId]
            : undefined,
        flexBenefits: EXTERNAL_BROCHURE_FLEX_BENEFITS
      }
    },
    {
      money: numberToCurrency,
      asMoney: moneyFormat,
      getBadgeDental: planName => getBadgeDental(planName, state.hcStorage.members),
      isRecommended: planName => /select/i.test(planName) || /value/i.test(planName),
      getDentalFees: (dental, vision) => detailsDentalFees(plans[dental], plans[vision]),
      DENTAL_PRODUCT_ID: DENTAL,
      hasSelectedVision: dentalId =>
        hasSelectedVision({
          dentalId,
          planId,
          selectedAddons,
          hasRenaissanceVision,
          renaissanceDentalPlanIds
        })
    }
  )
}

function renderMultiCarrierPlan(template, plans, state, planIdToShow) {
  const { primaryBenefits, secondaryBenefits } = getPlanBenefits(plans[0])
  const planOrders = Object.entries(state.multiCarrierOrder).map(order => order[1])
  const defaultPlanId = planIdToShow ? planIdToShow : plans[0].planId
  const relevantOrder = planOrders.find(order => order.includes(defaultPlanId))
  const plansAggregate = relevantOrder.map(planId =>
    plans.find(plan => plan.planId === planId)
  )
  const plan = plansAggregate[0]
  const planId = plan.planId

  const productName = toProductName(plan.productId)
  const planFilterKey = getPlanTypeKey(plan)
  const period = propAccessor(
    state.filtersByProductType[planFilterKey],
    'period',
    state.period
  )
  const visited = state.plansVisited.includes(`${planId}-${period}`)

  const validatedPeriodKey = +getValidPeriodKey(plan, period)

  const planDuration = getCorrespondingDuration({
    plan,
    mainPeriod: period === validatedPeriodKey ? period : validatedPeriodKey,
    additionalPeriods: state.additionalPeriods
  })

  const policy = planDuration

  const enrollment = policy.monthly.fees
    .filter(({ name }) => name === 'Application Fee')
    .reduce((acc, fee) => acc + fee.value, 0)

  const recurrence = plansAggregate[0].durations[0].recurrence

  let comparePlansRoute = `${COMPARE_PLANS}?plans=`
  plansAggregate.forEach(plan => {
    comparePlansRoute += `${plan.planId}-${plan.durations[0].policyDuration}-1,`
  })
  comparePlansRoute = comparePlansRoute.slice(0, -1)

  // Stuff to make add-ons work
  const { plansByType, plans: allPlans, addonsByPlan, brochures } = state

  // DENTAL VISION
  const primaryDnvIds = PRIMARY_DNV_ADD_ONS.flatMap(
    productType => plansByType[productType] || []
  )
  const dentalValuePlan = primaryDnvIds.find(planId =>
    /value/i.test(allPlans[planId].planName)
  )
  const primaryDnvPlans = dentalValuePlan
    ? [dentalValuePlan, ...primaryDnvIds.filter(id => id !== dentalValuePlan)]
    : primaryDnvIds
  const renaissanceDentalPlanIds = plansByType[DENTAL] || []
  const defaultRenaissanceDentalId = dentalValuePlan || renaissanceDentalPlanIds[0]
  const selectedAddons = propAccessor(addonsByPlan, String(planId), [])
  const ameritasDentalPlanIds = plansByType[AMERITAS_DENTAL] || []
  const ameritasVisionPlanIds = plansByType[AMERITAS_VISION] || []
  const renaissanceVisionPlan = state.plans[VISION_ID]
  const hasRenaissanceVision = renaissanceVisionPlan != null

  const renderAddons =
    state.eligibilityMonths === 8 && state.hcStorage.location.state_acronym === 'MO'

  // LATITUDE
  const latitudePlanIds = propAccessor(plansByType, String(LATITUDE), [])
  const latitudePlanId = latitudePlanIds[0]

  return template(
    {
      plan,
      plans: allPlans,
      state,
      defaultPlanId,
      primaryBenefits,
      secondaryBenefits,
      enrollment,
      productName,
      planFilterKey,
      visited,
      tooltip: {
        planDescription: getPlanDetailTooltip(plan, isEpicPlan(plan)),
        cost: tooltipMessage(state, plan, policy['monthly']),
        ...getBenefitsTooltips()
      },
      period,
      recurrence,
      expanded: true,
      comparePlansRoute,
      renderDental: true,
      renderLatitude: !enableFlexBenefits(),
      renderFlexBenefits: enableFlexBenefits(),
      externalPlansSelected: state.externalPlansSelected,
      externalPlans: state.externalPlans.map(externalPlan => ({
        planId: externalPlan.id,
        isRecommended: externalPlan.altName === 'FlexProtectPro',
        planName: externalPlan.name,
        totalAmount: externalPlan.products.reduce((sum: number, product: any) => {
          return sum + (product?.price || 0)
        }, 0),
        benefits: parseBenefits(externalPlan.products)
      })),
      primaryDnvPlans,
      latitudePlans: plansByType[LATITUDE] || [],
      selectedAddons: selectedAddons || [],
      hasRenaissanceVision,
      renaissanceVisionPlan,
      renderAddons: !renderAddons,
      brochures: {
        renaissanceDental: defaultRenaissanceDentalId
          ? brochures[allPlans[defaultRenaissanceDentalId].brochureId]
          : undefined,
        latitude: latitudePlanId
          ? brochures[allPlans[latitudePlanId].brochureId]
          : undefined,
        ameritasDental:
          ameritasDentalPlanIds.length > 0
            ? brochures[allPlans[ameritasDentalPlanIds[0]].brochureId]
            : undefined,
        ameritasVision:
          ameritasVisionPlanIds.length > 0
            ? brochures[allPlans[ameritasVisionPlanIds[0]].brochureId]
            : undefined
      }
    },
    {
      money: numberToCurrency,
      asMoney: moneyFormat,
      isRecommended: planName => /select/i.test(planName) || /value/i.test(planName),
      hasSelectedVision: dentalId =>
        hasSelectedVision({
          dentalId,
          planId,
          selectedAddons,
          hasRenaissanceVision,
          renaissanceDentalPlanIds
        }),
      getBadgeDental: planName => getBadgeDental(planName, state.hcStorage.members),
      DENTAL_PRODUCT_ID: DENTAL
    }
  )
}

function renderPlans(container, template, state, plansToShow, useMaxPolicy = false) {
  container.innerHTML = plansToShow
    .map((planId, index) => renderPlan(template, planId, state, index, useMaxPolicy))
    .join('')
}

function renderMultiCarrierPlans(container, template, state, plansToShow) {
  container.innerHTML = renderMultiCarrierPlan(template, plansToShow, state, undefined)
}

export { renderPlans, renderPlan, renderMultiCarrierPlans, renderMultiCarrierPlan }
